import React, { useEffect, useRef, useState } from 'react';
import { Engine, Scene, GaussianSplattingMesh } from '@babylonjs/core';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import '@babylonjs/loaders';

const CubeScene: React.FC = () => {
  const reactCanvas = useRef<HTMLCanvasElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingPercentage, setLoadingPercentage] = useState(0);
  const [currentModelUrl, setCurrentModelUrl] = useState("https://ghafari.co.uk/gsplat/model_1.splat"); // Default to Scene 1
  const [sceneInstance, setSceneInstance] = useState<Scene | null>(null);
  const [engineInstance, setEngineInstance] = useState<Engine | null>(null);
  const [gsMesh, setGsMesh] = useState<GaussianSplattingMesh | null>(null); // To keep track of the current mesh

  useEffect(() => {
    if (!reactCanvas.current) return;

    // Create Babylon.js engine and scene
    const engine = new Engine(reactCanvas.current, true);
    const scene = new Scene(engine);
    scene.clearColor = new Color4(0.8, 0.8, 0.8, 1); // Background color

    setSceneInstance(scene);
    setEngineInstance(engine);

    // Create ArcRotateCamera
    const camera = new ArcRotateCamera(
      'camera1',
      Math.PI / 2,
      Math.PI / 2,
      4,
      Vector3.Zero(),
      scene
    );
    camera.attachControl(reactCanvas.current, true);

    camera.wheelDeltaPercentage = 0.01;
    camera.lowerRadiusLimit = 2;
    camera.allowUpsideDown = false;

    // Create light
    const light = new HemisphericLight('light', new Vector3(1, 1, 0), scene);
    light.intensity = 0.7;

    // Create cube (box)
    const box = MeshBuilder.CreateBox('box', { size: 1 }, scene);

    // Start rendering the scene
    engine.runRenderLoop(() => {
      scene.render();
    });

    // Handle resize
    const handleResize = () => {
      engine.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      engine.dispose();
    };
  }, []);

  // Load model whenever currentModelUrl changes
  useEffect(() => {
    if (!sceneInstance || !currentModelUrl) return;

    const loadModel = async (url: string) => {
      try {
        setIsLoading(true);
        setLoadingPercentage(0);

        // Fetch the model file and track progress
        const modelBlob = await loadSplatModelWithProgress(url);

        // Dispose of the previous mesh if it exists
        if (gsMesh) {
          gsMesh.dispose();
        }

        // Create a new Gaussian Splatting Mesh
        const gs = new GaussianSplattingMesh("Halo", null, sceneInstance, true);
        const fileUrl = URL.createObjectURL(modelBlob);

        // Load the blob as a file into the Gaussian Splatting Mesh
        await gs.loadFileAsync(fileUrl);

        // Position the mesh correctly after it's loaded
        gs.position.y = 1.7;

        // Update the gsMesh state
        setGsMesh(gs);

        // Now that the mesh is fully loaded, hide the loading screen
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading splat model:", error);
      }
    };

    // Custom function to load the file and track the download progress
    const loadSplatModelWithProgress = async (url: string) => {
      const response = await fetch(url);
      const reader = response.body?.getReader();
      const contentLength = +response.headers.get('Content-Length')!;

      let receivedLength = 0;
      const chunks: Uint8Array[] = [];

      while (true) {
        const { done, value } = await reader?.read()!;
        if (done) break;

        chunks.push(value!);
        receivedLength += value!.length;

        // Calculate and update loading percentage
        const progress = Math.floor((receivedLength / contentLength) * 100);
        setLoadingPercentage(progress);
      }

      // Concatenate chunks into a single Uint8Array
      const chunksAll = new Uint8Array(receivedLength);
      let position = 0;
      for (let chunk of chunks) {
        chunksAll.set(chunk, position);
        position += chunk.length;
      }

      // Return the loaded file as a Blob
      return new Blob([chunksAll]);
    };

    loadModel(currentModelUrl);
  }, [currentModelUrl, sceneInstance]);

  // Handlers for button clicks
  const handleScene1 = () => {
    setCurrentModelUrl("https://ghafari.co.uk/gsplat/model_1.splat");
  };

  const handleScene2 = () => {
    setCurrentModelUrl("https://ghafari.co.uk/gsplat/model_2.splat");
  };

  const handleScene3 = () => {
    setCurrentModelUrl("https://ghafari.co.uk/gsplat/model_3.splat");
  };

  return (
    <>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100vh',
            backgroundColor: 'Grey',
            color: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            fontSize: '24px',
            zIndex: 1
          }}
        >
          <div>Splat is loading...</div>
          <div>{loadingPercentage}%</div>
        </div>
      )}
      {/* Canvas */}
      <canvas ref={reactCanvas} style={{ width: '100%', height: '100vh' }} />

      {/* Buttons */}
      <div
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 2
        }}
      >
        <button onClick={handleScene1} style={{ marginBottom: '10px' }}>
          Scene 1
        </button>
        <button onClick={handleScene2} style={{ marginBottom: '10px' }}>
          Scene 2
        </button>
        <button onClick={handleScene3}>
          Scene 3
        </button>
      </div>
    </>
  );
};

export default CubeScene;
